import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { FlagService } from '../../../../flag/flag.service';
import { SettingService } from '../../../../setting/setting.service';
import { dateAsString } from '../../../../shared/date.helper';
import { Inspection, InspectionItem, ItemInfo } from '../../../../shared/interfaces';
import { VersionService } from '../../../../version/version.service';
import { ModalDirective } from 'ngx-bootstrap/modal';

@Component({
  selector: 'app-inspection-comments',
  templateUrl: './inspection-comments.component.html',
  styleUrls: ['./inspection-comments.component.scss'],
})
export class InspectionCommentsComponent implements OnInit, OnDestroy, OnChanges {
  constructor(
    private flagService: FlagService,
    private versionService: VersionService,
    public settingService: SettingService,
    private router: Router,
  ) {}

  async ngOnInit() {
    await this.loadItems();
    await this.mergeAnswers();
    await this.loadComments();
    this.updateFilteredComments();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.answers) {
      this.loadComments();
      this.updateFilteredComments();
    }
  }

  get showOnlyReviewersComments() {
    return this._showOnlyReviewersComments;
  }

  set showOnlyReviewersComments(filter) {
    this._showOnlyReviewersComments = filter;
    this.updateFilteredComments();
  }

  @Input() answers;
  @Input() inspection: Inspection;
  @Output() allCommentsLength: EventEmitter<number> = new EventEmitter();

  @ViewChild('modalRef', { static: false }) modalRef!: ModalDirective;

  dateAsString = dateAsString;

  items: Array<InspectionItem & { info?: ItemInfo; label?: string; show?: boolean; index?: number }> = [];
  sections = [];
  questions = [];
  question = null;
  allComments = [];
  totalComments = 0;
  totalFlags = 0;
  countAllComments = 0;
  filteredComments = [];
  _showOnlyReviewersComments = false;
  destroyed;

  updateFilteredComments() {
    if (this.showOnlyReviewersComments) {
      const ret = this.allComments.map(c => ({ ...c }));

      ret.forEach(section => {
        section.comments = section.comments.filter(c => c.userId !== this.inspection.inspectorId);
      });
      this.filteredComments = ret.filter(s => s.comments.length);
    } else {
      this.filteredComments = this.allComments;
    }
    const totalComments = this.filteredComments.reduce((acc, item) => acc + item.comments.length, 0);
    this.allCommentsLength.emit(totalComments);
    this.countAllComments = totalComments;
  }

  ngOnDestroy() {
    // Avoid debounced method running after the component has been destroyed
    this.destroyed = true;
  }

  async loadItems() {
    this.items = [];

    const items: Array<InspectionItem & { info?: ItemInfo; label?: string; show?: boolean; index?: number }> = (
      await this.versionService.tree(this.inspection.versionId)
    ).items;

    // filter empty sections
    items.forEach(item => {
      item.sections = item.sections.filter(section => section.questions.length);
    });

    // filter empty items
    items.forEach((item, i) => {
      if (!item.sections.length) {
        delete items[i];
      } else {
        item.info = this.inspection.items.find(info => info.itemId === item.id);
      }
    });

    items.forEach(item => {
      if (!item.info) {
        return;
      }

      if (item.info.quantity === 1) {
        const customName = item.info.names && item.info.names[0];
        const defaultName = item.name;
        const label = customName ? `${customName} (${defaultName})` : defaultName;

        this.items.push({
          ...item,
          label,
        });
      } else if (item.info.quantity > 1) {
        for (let i = 0; i < item.info.quantity; i++) {
          const customName = item.info.names && item.info.names[i];
          const defaultName = `${item.name} # ${i + 1}`;
          const label = customName ? `${customName} (${defaultName})` : defaultName;

          this.items.push({
            // deep copy
            ...JSON.parse(JSON.stringify(item)),
            label,
            index: i + 1,
          });
        }
      }
    });
  }

  async mergeAnswers() {
    const subtypeInfo = await this.flagService.subtypeInfo;

    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;
      const buildingIndex = item.index;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        for (const iQuestion in questions) {
          const question = questions[iQuestion];

          if (question.repeat && question.repeat.length) {
            question.repeat.forEach(itemId => {
              const filteredAnswers = this.answers.filter(ans => {
                return ans.questionId === question.id;
              });

              question.answer = filteredAnswers;
            });
          } else {
            const answer = this.answers.find(ans => {
              const indexOk = !buildingIndex || ans.buildingIndex === buildingIndex;
              return ans.questionId === question.id && indexOk;
            });

            if (answer) {
              question.answer = answer;
            }
          }

          const answers = Array.isArray(question.answer) ? question.answer : [question.answer];

          answers.forEach(answer => {
            if (answer) {
              answer.question = question;

              // set images' path for tooltip and deeplink
              answer.pictures.forEach(pic => {
                pic.path = `${item.label} / ${section.name} / ${question.text}`;
                pic.deeplink = [+iItem, +iSection, +iQuestion];
              });

              // set flags & comments' deeplink
              [...answer.flags, ...answer.comments].forEach((x: any) => {
                x.deeplink = [+iItem, +iSection, +iQuestion];
              });

              // set flags subtype names
              answer.flags.forEach((x: any) => {
                if (x.subtype) {
                  x.subtype.name = this.flagService.getSubtypeInfo(subtypeInfo, x.subtype.name).name;
                }
              });
            }
          });
        }
      }
    }
  }

  getComments(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, cur) => [...acc, ...cur.comments], []);
      } else {
        return q.answer.comments;
      }
    }
    return [];
  }

  loadComments() {
    this.totalComments = 0;
    this.allComments = [];
    this.totalFlags = 0;

    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        const sectionComments = [];
        const sectionFlags = [];

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          const comments = this.getComments(question);
          const flags = this.getFlags(question);

          sectionFlags.push(
            ...flags.map(f => ({
              ...f,
              flag: f,
              question: question.text,
              hasPictures: this.nPictures(question),
              // AI-1178: "We also need to show sequential numbering for flags"
              index: ++this.totalFlags,
            })),
          );

          sectionComments.push(
            ...comments.map(f => ({
              ...f,
              question: question.text,
            })),
          );
        }

        if (sectionComments.length) {
          this.totalComments += sectionComments.length;

          this.allComments.push({
            label: `${item.label} / ${section.name}`,
            comments: sectionComments,
          });
          this.allCommentsLength.emit(this.allComments.length);
          this.countAllComments = this.allComments.length;
        }
      }
    }
  }

  nPictures(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, cur) => acc + cur.pictures.filter(p => !p.related).length, 0);
      } else {
        return q.answer.pictures.filter(p => !p.related).length;
      }
    }
    return 0;
  }

  getFlags(q) {
    // Ignoring related
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, ans) => [...acc, ...ans.flags.filter(f => f.answerId === ans.id)], []);
      } else {
        return q.answer.flags.filter(f => f.answerId === q.answer.id);
      }
    }
    return [];
  }

  openModal(): void {
    this.modalRef.show();
  }

  closeModal(): void {
    this.modalRef.hide();
  }

  navigateToQuestion(comment: any): void {
    if (this.inspection?.id && comment.deeplink) {
      const url = this.router.createUrlTree([`/inspection/review/main/${this.inspection.id}`], {
        queryParams: {
          item: comment.deeplink[0],
          section: comment.deeplink[1],
          question: comment.deeplink[2],
          right: 'question',
          clearFilters: 'true',
        },
      });

      window.open(this.router.serializeUrl(url), '_blank');
    }
  }
}
